import { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import useCalculatedNPVandPayback from '@/hooks/useCalculatedNPVandPayback';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { converToMillion } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: React.Key;
  name: React.ReactNode;
  total?: string;
}

const CashFlowStorageTable = ({
  hurdleRate,
  defaultHurdleRate,
}: {
  hurdleRate: number;
  defaultHurdleRate: number;
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const resultId = useParams<Record<string, string>>();
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId.resultId}/yearly_financial_analysis/`,
      {
        revalidateOnFocus: false,
      }
    );
  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId.resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );
  const changeInCashBalanceFlows =
    financialData?.graphData.map((item) => item.changeInCashBalanceOfStorage) ||
    [];

  const { calculatedNPV: adjustedNpvOfChangeInCashBalance, yearlyNPVs } =
    useCalculatedNPVandPayback(
      financialData?.graphData,
      changeInCashBalanceFlows,
      hurdleRate,
      defaultHurdleRate,
      simulationData?.result.npvOfChangeInCashBalanceOfStorage,
      simulationData?.result.discountedPaybackPeriodProjectStorage
    );

  if (!financialData || !simulationData) {
    return null;
  }

  const years = [
    ...new Set(financialData.graphData.map((item) => item.year)),
  ].sort((a, b) => a - b);

  const scrollX = 600 + years.length * 100;

  const getValuesForYears = (
    key: keyof SimulationResultYearlyFinancialAnalysisData
  ) => {
    const values: Record<string, string> = {};
    years.forEach((year) => {
      const data = financialData.graphData.find((item) => item.year === year);
      const yearKey = year.toString();

      if (
        key === 'npvOfChangeInCashBalanceOfStorage' &&
        yearlyNPVs[yearKey] !== undefined
      ) {
        values[yearKey] =
          `${converToMillion(Number(yearlyNPVs[yearKey]))} ${currencySymbol}`;
      } else {
        values[yearKey] = data
          ? `${converToMillion(data[key])} ${currencySymbol}`
          : `0 ${currencySymbol}`;
      }
    });
    return values;
  };

  const formatCurrency = (value?: number) =>
    `${converToMillion(value || 0)} ${currencySymbol}`;

  const columns: TableColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 350,
      fixed: 'left',
    },
    {
      title: t('result.finance.cashFlowTable.columns.total'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    ...years.map((year) => ({
      title: year.toString(),
      dataIndex: year.toString(),
      key: year.toString(),
      width: 100,
    })),
  ];

  const dataSource = [
    // Operating Cash Flow Section
    {
      key: '1',
      name: t('result.finance.cashFlowTable.rows.revenue'),
      total: `${converToMillion(simulationData?.result.revenueFromStorage || 0)} ${currencySymbol}`,
      ...getValuesForYears('revenueFromStorage'),
    },
    {
      key: '3',
      name: t('result.finance.cashFlowTable.rows.variableCostsOfStorage'),
      total: `${converToMillion(simulationData?.result.variableCostsOfStorage || 0)} ${currencySymbol}`,
      ...getValuesForYears('variableCostsOfStorage'),
    },
    {
      key: '4',
      name: t('result.finance.cashFlowTable.rows.opex'),
      total: `${converToMillion(simulationData?.result.storageOpex || 0)} ${currencySymbol}`,
      ...getValuesForYears('storageOpex'),
    },
    {
      key: '5',
      name: t('result.finance.cashFlowTable.rows.incomeTax'),
      total: `${converToMillion(simulationData?.result.incomeTaxStorage || 0)} ${currencySymbol}`,
      ...getValuesForYears('incomeTaxStorage'),
    },
    {
      key: '24',
      name: t('result.finance.cashFlowTable.rows.operatingCashFlow'),
      total: formatCurrency(simulationData?.result.operatingCashFlowStorage),
      ...getValuesForYears('operatingCashFlowStorage'),
    },
    // Investing Cash Flow Section
    {
      key: '9',
      name: t('result.finance.cashFlowTable.rows.storageCapitalExpenditures'),
      total: formatCurrency(simulationData?.result.storageCapitalExpenditures),
      ...getValuesForYears('storageCapitalExpenditures'),
    },
    {
      key: '10',
      name: t('result.finance.cashFlowTable.rows.investingCashFlow'),
      total: formatCurrency(simulationData?.result.investingCashFlowStorage),
      ...getValuesForYears('investingCashFlowStorage'),
    },
    {
      key: '2',
      name: t('result.finance.cashFlowTable.rows.residualValue'),
      total: `${converToMillion(simulationData?.result.residualValueOfStorage || 0)} ${currencySymbol}`,
      ...getValuesForYears('residualValueOfStorage'),
    },
    // Free Cash Flow to Firm
    {
      key: '11',
      name: t('result.finance.cashFlowTable.rows.freeCashFlowToFirm'),
      total: `${converToMillion(simulationData?.result.freeCashFlowToFirmOfStorage || 0)} ${currencySymbol}`,
      ...getValuesForYears('freeCashFlowToFirmOfStorage'),
    },
    // Financing Cash Flow Section
    {
      key: '12',
      name: t('result.finance.cashFlowTable.rows.loanProceeds'),
      total: formatCurrency(simulationData?.result.loanProceedsForStorage),
      ...getValuesForYears('loanProceedsForStorage'),
    },
    {
      key: '13',
      name: t('result.finance.cashFlowTable.rows.loanPrincipalPayment'),
      total: formatCurrency(
        simulationData?.result.loanPrincipalRepaymentForStorage
      ),
      ...getValuesForYears('loanPrincipalRepaymentForStorage'),
    },
    {
      key: '14',
      name: t('result.finance.cashFlowTable.rows.loanInterestPayment'),
      total: formatCurrency(
        simulationData?.result.loanInterestPaymentForStorage
      ),
      ...getValuesForYears('loanInterestPaymentForStorage'),
    },
    {
      key: '15',
      name: t('result.finance.cashFlowTable.rows.equityContribution'),
      total: formatCurrency(
        simulationData?.result.equityContributionForStorage
      ),
      ...getValuesForYears('equityContributionForStorage'),
    },
    {
      key: '16',
      name: t('result.finance.cashFlowTable.rows.financingCashFlow'),
      total: formatCurrency(simulationData?.result.financingCashFlowOfStorage),
      ...getValuesForYears('financingCashFlowOfStorage'),
    },
    // Cash Balance Section
    {
      key: '17',
      name: t('result.finance.cashFlowTable.rows.changeInCashBalance'),
      total: formatCurrency(
        simulationData?.result.changeInCashBalanceOfStorage
      ),
      ...getValuesForYears('changeInCashBalanceOfStorage'),
    },
    {
      key: '18',
      name: t('result.finance.cashFlowTable.rows.cashBalance'),
      total: formatCurrency(simulationData?.result.cashBalanceOfStorage),
      ...getValuesForYears('cashBalanceOfStorage'),
    },
    {
      key: '19',
      name: t('result.finance.cashFlowTable.rows.npvOfChangeInCashBalance'),
      total: formatCurrency(adjustedNpvOfChangeInCashBalance),
      ...getValuesForYears('npvOfChangeInCashBalanceOfStorage'),
    },
    // Free Cash Flow to Equity
    {
      key: '20',
      name: t('result.finance.cashFlowTable.rows.freeCashFlowToEquity'),
      total: formatCurrency(simulationData?.result.freeCashFlowToEquityStorage),
      ...getValuesForYears('freeCashFlowToEquityStorage'),
    },
    // IRR and Payback Section
    {
      key: '21',
      name: t('result.finance.cashFlowTable.rows.equityIrr'),
      total: `${(simulationData?.result.equityIrrStorage || 0).toFixed(2)}%`,
    },
    {
      key: '22',
      name: t('result.finance.cashFlowTable.rows.projectIrr'),
      total: `${(simulationData?.result.projectIrrStorage || 0).toFixed(2)}%`,
    },
    {
      key: '23',
      name: t('result.finance.cashFlowTable.rows.discountedPaybackPeriod'),
      total: `${simulationData?.result.discountedPaybackPeriodProjectStorage || 0} ${t('result.finance.cashFlowTable.rows.years')}`,
    },
  ];

  return (
    <StyledProfit>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: scrollX }}
      />
    </StyledProfit>
  );
};

export default CashFlowStorageTable;
